// Panel Clases en Vivo:
//   - consumerOnly / canProgram=false → solo calendario (filtrar, inscribirse, ver/Zoom).
//   - canProgram=true (adminliveclasses) → pestaña Programar + Calendario admin.

import { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { useI18n } from '../../context/useI18n';
import { hasSedeRole, isSedeFinalUser } from '../../utils/gymRoleScope';
import MastersBackHeader from '../common/MastersBackHeader';
import AdminLiveClasses from '../AdminLiveClasses';
import LiveClasses from '../LiveClasses';
import LiveClassRoutineHost from '../live/LiveClassRoutineHost';
import HostClassNoticeCards from '../live/HostClassNoticeCards';
import '../LiveClasses.css';
import '../d28d/d28d-admin-surfaces.css';

const TABS = {
  PROGRAM: 'program',
  CALENDAR: 'calendar',
};

const TZ_CO = 'America/Bogota';
const PLATFORM_PROGRAM_DEFAULTS = new Set(['virtual_d28d', 'vital', 'pancitas']);

function getAssignedProgramId(user) {
  if (!user) return null;
  const fromAccess = user.module_access?.d28d_program || user.module_access?.program;
  const programFromAccess = typeof fromAccess === 'string' && fromAccess.trim()
    ? fromAccess.trim()
    : null;
  return (
    user.program_id
    || user.programa_d28d
    || programFromAccess
    || null
  );
}

function resolveGymProgramId(user, gymPrograms = [], hostClasses = [], scopeGymId = null) {
  const raw = getAssignedProgramId(user);
  const gymId = scopeGymId || user?.gym_id || user?.gymId || null;

  if (hostClasses.length) {
    const fromClass = hostClasses.find((c) => c.program_id)?.program_id;
    if (fromClass) {
      if (gymPrograms.some((p) => p.id === fromClass)) return fromClass;
      if (!PLATFORM_PROGRAM_DEFAULTS.has(fromClass)) return fromClass;
    }
  }

  if (gymId && gymPrograms.length) {
    if (raw && gymPrograms.some((p) => p.id === raw)) return raw;
    const active = gymPrograms.find((p) => p.active !== false) || gymPrograms[0];
    if (raw && PLATFORM_PROGRAM_DEFAULTS.has(raw)) return active?.id || raw;
    if (!raw) return active?.id || null;
    return active?.id || raw;
  }

  return raw || null;
}

function formatHostClassLabel(title, startTime, lang = 'es', classFallback = 'Clase') {
  if (!startTime) return title || classFallback;
  try {
    const d = new Date(startTime);
    if (Number.isNaN(d.getTime())) return `${title || classFallback} — ${startTime}`;
    const locale = lang === 'en' ? 'en-US' : 'es-CO';
    const when = new Intl.DateTimeFormat(locale, {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: TZ_CO,
    }).format(d);
    return `${title || classFallback} — ${when}`;
  } catch {
    return `${title || classFallback} — ${startTime}`;
  }
}

function userIsD28dHost(user) {
  const roles = Array.isArray(user?.roles) ? user.roles : [];
  return roles.includes('entrenador_d28d')
    || roles.includes('entrenador_gym')
    || user?.rol === 'entrenador_d28d'
    || user?.rol === 'entrenador_gym';
}

export default function LiveClassesPanel({
  user = null,
  canProgram = false,
  consumerOnly = false,
  programId: forcedProgramId = null,
  scopeGymId = null,
  scopeGymName = '',
  onBack = null,
  defaultTab = 'calendar',
}) {
  const { t, lang } = useI18n();
  const isConsumer = consumerOnly || !canProgram;
  const isGymScoped = Boolean(scopeGymId);
  const initialTab = isConsumer ? TABS.CALENDAR : (defaultTab === 'program' ? TABS.PROGRAM : TABS.CALENDAR);
  const [tab, setTab] = useState(initialTab);
  const [programFormDirty, setProgramFormDirty] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(forcedProgramId || '');
  const [hostClasses, setHostClasses] = useState([]);
  const [hostClassId, setHostClassId] = useState('');
  const [hostNotifications, setHostNotifications] = useState([]);
  const isHostOnly = isConsumer && userIsD28dHost(user);
  const isSedeScope = hasSedeRole(user)
    || isSedeFinalUser(user)
    || user?.is_sede_final === true
    || user?.gym_parent_id != null;

  const userGymId = scopeGymId || user?.gym_id || user?.gymId || null;

  const resolvedProgramId = useMemo(() => {
    if (canProgram) return selectedProgram || null;
    if (isSedeScope) return null;
    return resolveGymProgramId(user, programs, hostClasses, userGymId)
      || forcedProgramId
      || null;
  }, [canProgram, selectedProgram, user, programs, hostClasses, userGymId, forcedProgramId, isSedeScope]);

  useEffect(() => {
    if (isConsumer) {
      setTab(TABS.CALENDAR);
    }
  }, [isConsumer]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const params = {};
        if (canProgram) {
          if (isGymScoped && scopeGymId) {
            params.gym_id = scopeGymId;
            params.gym_only = 'true';
          } else {
            params.platform_only = 'true';
          }
        } else if (userGymId) {
          params.gym_id = userGymId;
          params.gym_only = 'true';
        } else {
          return;
        }
        const r = await api.get('/programs', { params });
        const arr = r.data?.data || r.data || [];
        if (active && Array.isArray(arr)) setPrograms(arr);
      } catch {
        if (active) setPrograms([]);
      }
    })();
    return () => { active = false; };
  }, [canProgram, isGymScoped, scopeGymId, userGymId]);

  useEffect(() => {
    if (!isHostOnly) return;
    let active = true;
    (async () => {
      try {
        const n = await api.get('/notifications');
        if (active) setHostNotifications((n.data?.data || []).slice(0, 20));
      } catch {
        if (active) setHostNotifications([]);
      }
    })();
    return () => { active = false; };
  }, [isHostOnly]);

  useEffect(() => {
    if (!isHostOnly) return;
    let active = true;
    (async () => {
      try {
        const r = await api.get('/live-classes/admin');
        const list = r.data?.data || [];
        if (!active) return;
        setHostClasses(list);
        setHostClassId((prev) => prev || (list[0] ? String(list[0].id) : ''));
      } catch {
        if (active) setHostClasses([]);
      }
    })();
    return () => { active = false; };
  }, [isHostOnly]);

  const hostClass = hostClasses.find((c) => String(c.id) === String(hostClassId));

  const selectedProgramLabel = useMemo(() => {
    if (canProgram) {
      if (!selectedProgram) return t('live.all_programs', 'Todos los programas');
      const p = programs.find((x) => (x.id || x.code || x.slug) === selectedProgram);
      return p?.name || p?.title || p?.nombre || selectedProgram;
    }
    if (!resolvedProgramId) return null;
    const p = programs.find((x) => (x.id || x.code || x.slug) === resolvedProgramId);
    return p?.name || p?.title || p?.nombre || resolvedProgramId;
  }, [canProgram, selectedProgram, programs, resolvedProgramId, t]);

  const showAssignedProgram = Boolean(resolvedProgramId || (isHostOnly && hostClasses.length));

  const requestTab = (next) => {
    if (
      programFormDirty
      && tab === TABS.PROGRAM
      && next !== TABS.PROGRAM
      && !window.confirm(t('live.unsaved_confirm', 'Tienes cambios sin guardar en Programar. ¿Cambiar de pestaña sin guardar?'))
    ) {
      return;
    }
    setTab(next);
  };

  return (
    <div className={`dashboard-main-view${!isConsumer ? ' d28d-admin-surface surface-readable' : ''}`}>
      <header className="dashboard-header live-classes-panel__header mb-3">
        {onBack && isGymScoped ? (
          <MastersBackHeader onBack={onBack} title={t('live.title_admin', 'Clases en vivo')} />
        ) : null}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            {!(onBack && isGymScoped) ? (
              <h2 className="d28d-page-title">
                {isConsumer
                  ? t('live.title', 'Clases en Vivo')
                  : t('live.title_admin', 'Programar clases en vivo')}
              </h2>
            ) : null}
            <p className={`${onBack && isGymScoped ? 'text-sm text-stone-600' : 'd28d-text-muted'}`} style={{ margin: 0 }}>
              {isConsumer
                ? (isSedeScope
                  ? t('live.subtitle_sede', 'Calendario de clases globales de tu gimnasio virtual (programas activados en la sede).')
                  : (showAssignedProgram
                    ? t('live.subtitle_assigned', 'Calendario de tu programa asignado.')
                    : t('live.subtitle_user', 'Consulta el horario, filtra clases e inscríbete. Sin edición desde aquí.')))
                : t('live.subtitle_admin', 'Crea y edita sesiones. El calendario público está en Clases en Vivo del menú.')}
            </p>
          </div>
          {onBack && !isGymScoped ? (
            <button type="button" className="btn-secondary" onClick={onBack} aria-label={t('live.back', 'Volver')}>
              {t('live.back', '← Volver')}
            </button>
          ) : null}
        </div>
      </header>

      {!isConsumer && (
        <div
          role="tablist"
          aria-label={t('live.tab_views', 'Vistas de Clases en Vivo')}
          className="live-classes-panel__tabs"
        >
          <button
            type="button"
            role="tab"
            aria-selected={tab === TABS.PROGRAM}
            onClick={() => requestTab(TABS.PROGRAM)}
            className={`live-classes-panel__tab ${tab === TABS.PROGRAM ? 'live-classes-panel__tab--active' : ''}`}
          >
            {t('live.tab_program', 'Programar')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === TABS.CALENDAR}
            onClick={() => requestTab(TABS.CALENDAR)}
            className={`live-classes-panel__tab ${tab === TABS.CALENDAR ? 'live-classes-panel__tab--active' : ''}`}
          >
            {t('live.tab_calendar', 'Calendario')}
          </button>
        </div>
      )}

      {(isConsumer || tab === TABS.CALENDAR) && (
        <div
          className="surface-readable rounded-xl border border-slate-200 p-3 mb-3"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            flexWrap: 'wrap',
          }}
        >
          {canProgram && tab === TABS.CALENDAR ? (
            <>
              <label htmlFor="program-filter" className="ui-field__label mb-0">
                {t('live.program_label', 'Programa:')}
              </label>
              <select
                id="program-filter"
                value={selectedProgram}
                onChange={(e) => setSelectedProgram(e.target.value)}
                className="ui-input"
                style={{ maxWidth: 260, width: 'auto' }}
                aria-label={t('live.program_label', 'Programa:')}
              >
                <option value="">{t('live.all_programs', 'Todos los programas')}</option>
                {programs.map((p) => {
                  const id = p.id || p.code || p.slug;
                  const name = p.name || p.title || p.nombre || id;
                  return (
                    <option key={id} value={id}>{name}</option>
                  );
                })}
              </select>
              <span className="ui-field__hint">
                {t('live.showing', 'Mostrando: {label}', { label: selectedProgramLabel })}
              </span>
            </>
          ) : showAssignedProgram && !isSedeScope ? (
            <span className="ui-field__hint">
              {t('live.your_program', 'Tu programa: {label}', { label: selectedProgramLabel || resolvedProgramId })}
            </span>
          ) : isSedeScope ? (
            <span className="ui-field__hint">
              {programs.length
                ? t('live.sede_programs', 'Programas de la sede: {count}', { count: programs.length })
                : t('live.sede_programs_empty', 'Programas activados por el gimnasio virtual.')}
            </span>
          ) : (
            <span className="ui-field__hint">
              {t('live.filter_hint', 'Usa los filtros del calendario para encontrar tu clase.')}
            </span>
          )}
          <span className="ui-field__hint" style={{ marginLeft: 'auto' }}>
            {t('live.holiday_note', 'Los días festivos en Colombia tendrán una programación diferente.')}
          </span>
        </div>
      )}

      <div role="tabpanel">
        {!isConsumer && tab === TABS.PROGRAM && (
          <AdminLiveClasses
            embedded
            scopeGymId={scopeGymId}
            scopeGymName={scopeGymName}
            onDirtyChange={setProgramFormDirty}
          />
        )}

        {isHostOnly && hostNotifications.length > 0 && (
          <HostClassNoticeCards notices={hostNotifications} programs={programs} />
        )}

        {isHostOnly && hostClasses.length > 0 && (
          <div className="surface-readable ui-light-panel mb-4">
            <label className="ui-field__label">{t('live.host_class_readonly', 'Tu clase asignada (solo lectura)')}</label>
            <select className="ui-input max-w-md mb-3" value={hostClassId} onChange={(e) => setHostClassId(e.target.value)}>
              {hostClasses.map((c) => (
                <option key={c.id} value={c.id}>
                  {formatHostClassLabel(c.title, c.start_time, lang, t('live.host_class_fallback', 'Clase'))}
                </option>
              ))}
            </select>
            {hostClass && <LiveClassRoutineHost classItem={hostClass} user={user} />}
          </div>
        )}

        {(isConsumer || tab === TABS.CALENDAR) && (
          <LiveClasses
            key={isSedeScope ? 'sede-all' : (resolvedProgramId || 'all')}
            programId={resolvedProgramId}
            sedeMode={isSedeScope}
            canCloneWeek={Boolean(canProgram)}
            scopeGymId={scopeGymId}
          />
        )}
      </div>
    </div>
  );
}
